import React, { forwardRef } from "react";
import styled from "styled-components";
import IEditableProps from "../../../utilities/IEditableProps";
import IStyledProps from "../../../utilities/IStyledProps";
import classNames from "classnames";
import { getImagePath } from "../../../utilities/path";
import { AutomationTestIdFeature, AutomationTestIdElementType, createDataTestId } from "../../../utilities/automation";
import { usePageProps } from '../../../context/PagePropContext';
import Picture from "../../Picture";
import AdobeTarget from "../../AdobeTarget";
import renderContent from "../../../utilities/renderContent";

export interface IHeroGlassProps extends IEditableProps, IStyledProps {
    backgroundSrc: string;
    backgroundAlt: string;
    underhang: boolean;
    adobeWait: string;
    placeholderSrc: string;
    minHeight: number;
    heroSubTitle;
    heroTitle;
    heroBody;
    heroFooter;
    children;
}

const HeroGlass = forwardRef((props: IHeroGlassProps, ref: any) => {
    const { children, heroSubTitle, heroTitle, heroBody, heroFooter, editorClassObj, className, cmsId, minHeight = 0, adobeWait = 0, backgroundSrc, placeholderSrc = '' } = props;
    const { path } = usePageProps();
    const isMustReads = path.indexOf('must-reads') === 0 || path.indexOf('/must-reads') === 0;
    const mustReadsClassObj = { 'must-reads': isMustReads };

    const overlay = React.Children.map(children, child => {
        if (child.type.mdxType === 'HeroBar' || child.props.mdxType === 'HeroBar') {
            return null;
        }
        if (typeof child === "object") {
            return React.cloneElement(child, { className: classNames(child.props.className, 'overlay h-100 py-lg-6 px-xl-6 px-lg-5 px-4 py-5 flex-grow-1 align-items-center', {'d-flex': child.type.mdxType !== 'LoginBox' && child.props.mdxType !== 'LoginBox'}), type: "header1", style: 'object-fit: cover;'});
        }
    });

    const heroBar = React.Children.map(children, child => {
        if (child.type.mdxType === 'HeroBar' || child.props.mdxType === 'HeroBar') {
            return React.cloneElement(child, { className: classNames(child.props.className)});
        }
    });

    return (
        <div data-id={cmsId} ref={ref} className={classNames(className, editorClassObj, mustReadsClassObj, "d-flex flex-column")}>
            <div className="d-none d-lg-block hero-blur blur"></div>
            <Picture
                id="heroglass-target-image"
                className={"hero-bg"}
                adobeWait={cmsId ? 0 : Number(adobeWait)}
                placeholderSrc={getImagePath(placeholderSrc)}
                src={getImagePath(backgroundSrc)}
                alt={props.backgroundAlt}
                dataTestId={createDataTestId({ feature: AutomationTestIdFeature.HERO, element: AutomationTestIdElementType.IMAGE, specifier: 'hero-bg' })}
            />
            <div className="hero-top d-flex w-100 flex-grow-1">
                <div className={"d-flex hero-content container p-0"}>
                    <div className={classNames("hero-copy d-flex justify-content-center align-items-center w-50 h-100")}>
                        <AdobeTarget minHeight={Number(minHeight)} id="heroglass-target-text" cmsId={cmsId} editorClassObj={editorClassObj} adobeWait={props.adobeWait} className="container h-100 px-4 py-5 d-flex flex-column justify-content-center">
                            <h2 className="hero-sub-title text-uppercase mb-2" dangerouslySetInnerHTML={{ __html: heroSubTitle }}></h2>
                            <h1 className="hero-title display-4 mb-4" dangerouslySetInnerHTML={{ __html: heroTitle }}></h1>
                            <div className="hero-body mb-5">
                                {renderContent("HeroGlass", heroBody)}
                            </div>
                            <div className="hero-footer">
                                {renderContent("HeroGlass", heroFooter)}
                            </div>
                        </AdobeTarget>
                    </div>
                    {overlay}
                </div>
            </div>
            {(heroBar?.length > 0) &&
                <div className="hero-bottom container">{heroBar}</div>}
        </div>
    )
});

export default styled(HeroGlass)`
    position: relative;
    min-height: 580px;

    img.hero-bg {
        position: absolute;
        height: 100%;
        width: 100%;
        object-fit: cover;
    }
    .hero-content {
        position: relative;
        @media (max-width: 991px) {
            align-items: center;
            flex-direction: column;
            max-width: 100%;
        }
    }
    .hero-top {
        z-index: 1;
        @media (max-width: 991px) {
            -webkit-backdrop-filter: blur(10px);
            backdrop-filter: blur(10px);
        }
    }
    .hero-blur {
        position: absolute;
        height: 100%;
        width: 50%;
        z-index: 1;
    }
    .hero-copy {
        color: white;
        @media (max-width: 991px) {
            width: 100% !important;
        }
    }
    .hero-title {
        font-family: "National2";
        font-size: 3.333rem;
        font-weight: 350;
        line-height: 3.667rem;
        letter-spacing: -0.01rem;
    }
    .hero-body, .hero-footer {
        font-size: 0.889rem;
        font-weight: 400;
    }
    .hero-bottom {
        z-index: 1;
    }

    .overlay {
        @media (min-width: 991px) {
            width: 50% !important;
        }
    }
`;